import { defineStore } from 'pinia'
import { getMe, login as svcLogin } from '@/services/auth'
import { getAccessToken, getRefreshToken, getSessionVersion, setTokens, clearTokens } from '@/auth/storage'
import { useEnrollmentsStore } from '@/stores/enrollments'
import { useProgressStore } from '@/stores/progress'
import { useCoursesStore } from '@/stores/courses'
import { getApiBase } from '@/utils/url'

const loads = new WeakMap()

const normalizeRole = (r) => {
  const v = String(r || '').toLowerCase()
  if (v === 'teacher' || v === 'lecturer' || v === 'staff') return 'lecturer'
  if (v === 'student') return 'student'
  return 'student'
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    role: '',
    username: '',
    loaded: false,
    loading: false,
    loginLoading: false,
  }),
  getters: {
    isAuthenticated: (state) => !!state.user,
    isLecturer: (state) => normalizeRole(state.role) === 'lecturer',
    isTeacher: (state) => normalizeRole(state.role) === 'lecturer',
  },
  actions: {
    ensureLoaded() {
      const token = getAccessToken()
      if (!token) {
        this.reset()
        this.loaded = true
        return Promise.resolve()
      }
      if (this.loaded && this.user) return Promise.resolve(this.user)
      if (loads.has(this)) return loads.get(this)
      const version = getSessionVersion()
      this.loading = true
      const promise = getMe().then(data => {
        if (version !== getSessionVersion()) return null
        if (!data?.username) throw new Error('Unable to load your account. Please try again.')
        this.user = data
        this.role = normalizeRole(data.role)
        this.username = data.username
        this.loaded = true
        return data
      }).catch(error => {
        if (version === getSessionVersion() && [401, 403].includes(error?.response?.status)) this.logout()
        // Network/server failures do not invalidate valid credentials or report a successful login.
        throw error
      }).finally(() => {
        if (loads.get(this) === promise) {
          loads.delete(this)
          this.loading = false
        }
      })
      loads.set(this, promise)
      return promise
    },
    async login({ username, password, remember }) {
      if (this.loginLoading) return
      this.loginLoading = true
      let version = getSessionVersion()
      try {
        const res = await svcLogin(String(username || '').trim(), String(password || ''))
        if (version !== getSessionVersion()) throw new Error('Sign-in was interrupted. Please sign in again.')
        setTokens({ access: res?.access, refresh: res?.refresh, remember: remember === true })
        version = getSessionVersion()
        this.reset()
        this.loginLoading = true
        useEnrollmentsStore().clear()
        useProgressStore().clear()
        useCoursesStore().clear()
        await this.ensureLoaded()
        if (version !== getSessionVersion() || !this.user) throw new Error('Sign-in was interrupted. Please sign in again.')
        return res
      } finally {
        if (version === getSessionVersion()) this.loginLoading = false
      }
    },
    reset() {
      loads.delete(this)
      this.user = null
      this.role = ''
      this.username = ''
      this.loaded = false
      this.loading = false
      this.loginLoading = false
    },
    logout() {
      const access = getAccessToken()
      const refresh = getRefreshToken()
      const revoke = access ? fetch(`${getApiBase()}logout/`, {
        method: 'POST', headers: { Authorization: `Bearer ${access}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }), keepalive: true,
      }).catch(() => null) : Promise.resolve()
      clearTokens()
      this.reset()
      useEnrollmentsStore().clear()
      useProgressStore().clear()
      useCoursesStore().clear()
      return revoke
    }
  },
  persist: false
})
